import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAppSelector } from '../store/hooks';
import AsyncSearchSelect, { type SelectOption } from './AsyncSearchSelect';
import LoadingSpinner from './LoadingSpinner';
import RewardSubscriptionBox from './RewardSubscriptionBox';
import type { MentionEligibility, Student, StudentSubscription } from '../store/slices/studentsSlice';
import './GroupForm.css';
import './RewardSubscriptionBox.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

type StudentOption = SelectOption<number> & { student?: Student };

function studentName(student: Pick<Student, 'id' | 'firstName' | 'lastName' | 'surname'>) {
  return [student.firstName, student.lastName, student.surname].filter(Boolean).join(' ') || `Student #${student.id}`;
}

function eligibilityCopy(eligibility: MentionEligibility) {
  if (eligibility.status === 'allowed' && eligibility.giveaway) {
    return `Eligible for “${eligibility.giveaway.name}”. Mentions accepted until ${eligibility.giveaway.mentionUntil}.`;
  }
  if (eligibility.reason === 'already_rewarded' && eligibility.giveaway) {
    return `Already rewarded for “${eligibility.giveaway.name}”.`;
  }
  if (eligibility.reason === 'not_in_group') {
    return 'This student is not in a group, so they cannot join a giveaway.';
  }
  return 'This student is not in a group currently running a giveaway.';
}

const MentionRewardForm: React.FC = () => {
  const { studentId: routeStudentId, subscriptionId } = useParams<{
    studentId?: string;
    subscriptionId?: string;
  }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = useAppSelector((state) => state.auth.token);
  const initialStudentId = routeStudentId || searchParams.get('studentId') || '';
  const isEditing = Boolean(subscriptionId);

  const [selectedStudent, setSelectedStudent] = useState<StudentOption | null>(null);
  const [student, setStudent] = useState<Student | null>(null);
  const [existing, setExisting] = useState<StudentSubscription | null>(null);
  const [eligibility, setEligibility] = useState<MentionEligibility | null>(null);
  const [loading, setLoading] = useState(Boolean(initialStudentId));
  const [error, setError] = useState('');

  const loadStudents = useCallback(async (search: string): Promise<StudentOption[]> => {
    const response = await axios.get(`${API_BASE_URL}/giveaways/eligible-students`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { search, limit: 100 },
    });
    return (response.data.students || []).map((item: Student) => ({
      value: item.id,
      label: studentName(item),
      student: item,
    }));
  }, [token]);

  const loadStudent = useCallback(async (id: number) => {
    setLoading(true);
    setError('');
    try {
      const [studentResponse, subscriptionsResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/students/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_BASE_URL}/students/${id}/subscriptions`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      const loadedStudent = studentResponse.data.student as Student;
      const subscriptions = (subscriptionsResponse.data.subscriptions || []) as StudentSubscription[];
      const loadedExisting = subscriptionId
        ? subscriptions.find((sub) => sub.id === Number(subscriptionId) && sub.subscriptionType?.rewardKind === 'mention') || null
        : null;

      setStudent(loadedStudent);
      setSelectedStudent({ value: loadedStudent.id, label: studentName(loadedStudent), student: loadedStudent });
      setEligibility(loadedStudent.mentionEligibility || null);
      setExisting(loadedExisting);
      if (subscriptionId && !loadedExisting) setError('Mention reward not found');
    } catch (err) {
      setError(axios.isAxiosError(err) ? err.response?.data?.error || err.message : 'Failed to load student');
      setStudent(null);
      setExisting(null);
      setEligibility(null);
    } finally {
      setLoading(false);
    }
  }, [subscriptionId, token]);

  useEffect(() => {
    if (!initialStudentId) return;
    void (async () => {
      await loadStudent(Number(initialStudentId));
    })();
  }, [initialStudentId, loadStudent]);

  const handleStudentChange = (option: StudentOption | null) => {
    setSelectedStudent(option);
    setStudent(null);
    setExisting(null);
    setEligibility(null);
    if (option) void loadStudent(option.value);
  };

  const returnPath = student ? `/mentions?studentId=${student.id}` : '/mentions';
  const canShowForm = Boolean(
    student
    && (!isEditing || existing)
    && (isEditing || eligibility?.status === 'allowed')
  );

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>{isEditing ? 'Edit Mention' : 'Add Mention'}</h1>
          <p>{isEditing ? 'Update proof and reward subscription details' : 'Record proof and grant the mention reward'}</p>
        </div>
        <button type="button" className="btn-secondary" onClick={() => navigate(returnPath)}>Cancel</button>
      </div>

      <div className="consultation-edit">
        <section className="consultation-edit__card">
          <h2 className="consultation-edit__card-title">Student</h2>
          <div className="form-group">
            <label>Student *</label>
            <AsyncSearchSelect<number, StudentOption>
              value={selectedStudent}
              onChange={handleStudentChange}
              loadOptions={loadStudents}
              placeholder="Choose a student in a running giveaway…"
              noOptionsMessage="No eligible students"
              isDisabled={isEditing}
            />
          </div>
        </section>

        {loading && <LoadingSpinner />}
        {error && <div className="error-message">{error}</div>}

        {!isEditing && student && eligibility && eligibility.status !== 'allowed' && (
          <section className="consultation-edit__card">
            <div className="mention-eligibility mention-eligibility--blocked">
              <strong>Mention not available.</strong> {eligibilityCopy(eligibility)}
            </div>
          </section>
        )}

        {canShowForm && student && (
          <RewardSubscriptionBox
            kind="mention"
            recipientStudentId={student.id}
            recipientName={studentName(student)}
            existing={existing}
            giveawayName={eligibility?.giveaway?.name}
            onDismiss={() => navigate(returnPath)}
            onSuccess={() => navigate(returnPath)}
          />
        )}
      </div>
    </div>
  );
};

export default MentionRewardForm;
